import React, { useRef, useState } from 'react'
import shallow from 'zustand/shallow'
import { Button } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro'
import { useStore } from '../../store/store';
import { getBoxesByColumnId, getContainerById, getContainerByColumnId } from '../../store/lookups';
import { antdFormError, containerBackground, minContainerHeight, pageBackground } from '../../constants/constants';
import { rowAdderBandGeometry } from '../../utils/styles';
import { containerColumnGap, getTopLevelContainer } from '../../utils/columns';
import { useResizeHandleClearance } from '../common/useResizeHandleClearance';
import ColumnWrapper from './ColumnWrapper';

const selector = (rowContainerId) => ({ rowContainers, boxes, addRowToColumn, removeRowFromColumn }) => {
  const row = getContainerById(rowContainers, rowContainerId);
  return {
    columnsIds: row?.columnsIds,
    hasBox: !!row?.columnsIds.some((cid) => getBoxesByColumnId(boxes, cid).length > 0),
    addRowToColumn,
    removeRowFromColumn,
  }
};

const toolbarButtonStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 18,
  height: 18,
  minWidth: 18,
  padding: 0,
}

// Bouton « + » de la bande d'ajout : même geste que l'ajout de container entre
// deux blocs, mais deux fois plus petit — une ligne est un niveau plus fin, et
// la pastille de 30px du document chargeait l'intérieur du container.
const adderButtonStyle = {
  width: 20,
  height: 20,
  minWidth: 20,
  minHeight: 20,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
}

/**
 * Une ligne empilée à l'intérieur d'une colonne : rend ses sous-colonnes côte
 * à côte, comme Container le fait pour un container top-level. `parentWidthPx`
 * est la largeur disponible de la colonne porteuse — les sous-colonnes s'y
 * partagent la place au lieu de se référer à la largeur de page.
 *
 * Le nombre de sous-colonnes se règle depuis le panel de droite
 * (ContainerLayoutPanel) : ici on ne garde que l'ajout/suppression de ligne
 * et la suppression d'une sous-colonne, qui gagnent à rester au contact du
 * contenu.
 */
const ColumnsRow = React.memo(({
  rowContainerId,
  parentColumnId,
  rowIndex,
  isLastRow,
  parentWidthPx,
  showCellOutline,
  fromViewer,
  setIsResizingContainer,
}) => {
  const {
    columnsIds,
    hasBox,
    addRowToColumn,
    removeRowFromColumn,
  } = useStore(selector(rowContainerId), shallow);
  const lineSpacing = useStore(
    ({ configuration, containers }) =>
      getContainerByColumnId(containers, parentColumnId)?.lineSpacing ??
      configuration.lineSpacing
  )
  // Écart du container top-level : une ligne imbriquée ne se règle pas seule.
  const columnGap = useStore(({ containers, rowContainers }) =>
    containerColumnGap(
      getTopLevelContainer({ columnId: parentColumnId, containers, rowContainers })
    )
  )
  const fromLinkedSection = useStore(
    ({ containers }) =>
      !!getContainerByColumnId(containers, parentColumnId)?.from_linked_section
  )
  const unlockProposal = useStore(({ configuration }) => configuration.unlockProposal)
  const isTemplate = useStore(({ currentDocument }) => currentDocument.type === 'template')
  const fromPdf = useStore(({ fromPdf }) => fromPdf)

  const [isHovered, setIsHovered] = useState(false)
  const [isAdderHovered, setIsAdderHovered] = useState(false)
  const [isDeleteHovered, setIsDeleteHovered] = useState(false)

  // Section liée : contenu en lecture seule (cf. denyIfLinkedSection), on ne
  // montre donc ni l'ajout ni la suppression de ligne.
  const canEditRow =
    (isTemplate || unlockProposal) && !fromPdf && !fromViewer && !fromLinkedSection
  const showToolbar = canEditRow && isHovered
  const adderBandRef = useRef(null)
  const adderClearance = useResizeHandleClearance({
    bandRef: adderBandRef,
    active: showToolbar,
    plusSize: adderButtonStyle.width,
  })

  if (!columnsIds?.length) return null;

  // Une sous-colonne seule ne se retire pas : c'est alors la ligne entière.
  const canRemoveColumn = canEditRow && columnsIds.length > 1
  // La bande d'ajout se pose dans la gouttière entre deux lignes (cf. rowGap
  // dans Column.jsx, piloté par « Marge entre les lignes ») : en absolu, elle
  // ne décale rien. Hauteur plafonnée et cas de la dernière ligne : cf.
  // rowAdderBandGeometry.
  const { height: adderBandHeight, bottom: adderBandBottom } =
    rowAdderBandGeometry({ lineSpacing, isLastRow })

  return <div
    data-role={pageBackground}
    data-parent={containerBackground}
    onMouseOver={() => setIsHovered(true)}
    onMouseLeave={() => {
      setIsHovered(false)
      setIsDeleteHovered(false)
    }}
    style={{
      display: 'flex',
      width: '100%',
      position: 'relative',
      // Comme entre les colonnes d'un container (cf. Container.jsx).
      columnGap,
      // Même règle qu'un container top-level (cf. Container.jsx) : une ligne
      // vide doit rester visible et survolable pour accueillir un drop.
      minHeight: hasBox ? undefined : minContainerHeight,
    }}
  >
    {columnsIds.map((colId, index) => (
      <ColumnWrapper
        id={colId}
        key={colId}
        isNested
        parentWidthPx={parentWidthPx}
        showCellOutline={showCellOutline}
        containerColumnsLength={columnsIds.length}
        setIsResizingContainer={setIsResizingContainer}
        fromViewer={fromViewer}
        isLastColumn={index === columnsIds.length - 1}
        isFirstColumn={index === 0}
        isMiddleColumn={index > 0 && index < columnsIds.length - 1}
        canRemoveFromRow={canRemoveColumn}
      />
    ))}
    {/* Ligne à supprimer en rouge au survol du « × », comme une ligne de
        tableau (cf. .table-border-clicked-to-delete). */}
    {showToolbar && isDeleteHovered && <div style={{
      position: 'absolute',
      inset: 0,
      zIndex: 3,
      pointerEvents: 'none',
      border: `1px solid ${antdFormError}`,
      backgroundColor: 'rgba(255, 77, 79, 0.3)',
    }} />}
    {showToolbar && <div
      data-outside-ignore="true"
      style={{
        position: 'absolute',
        top: 2,
        // Collé au coin : la bande de bordure droite de la colonne porteuse
        // (BorderColumn) laisse passer le survol hors glisser.
        right: 2,
        zIndex: 3,
        display: 'flex',
        gap: 2,
      }}
    >
      <Button
        title="Supprimer la ligne"
        shape='circle'
        style={{
          ...toolbarButtonStyle,
          backgroundColor: isDeleteHovered ? antdFormError : undefined,
          borderColor: isDeleteHovered ? 'white' : undefined,
        }}
        onMouseEnter={() => setIsDeleteHovered(true)}
        onMouseLeave={() => setIsDeleteHovered(false)}
        // Centrage absolu (le bouton antd est en position: relative) : le
        // centrage flex laissait la croix décalée d'un pixel en haut à gauche.
        icon={<FontAwesomeIcon icon={icon({ name: 'xmark', style: 'solid' })} style={{
          fontSize: 10,
          display: 'block',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: isDeleteHovered ? 'white' : undefined,
        }} />}
        onClick={(e) => {
          e.stopPropagation();
          removeRowFromColumn({ columnId: parentColumnId, rowContainerId })
        }}
      />
    </div>}

    {/* Ajout d'une ligne : bande sous la ligne survolée, avec un « + » centré —
        même geste que l'ajout d'un container entre deux blocs du document. Le
        bouton vivait en haut à droite, où « la ligne suivante » ne se lisait pas.
        Teinte volontairement plus légère que celle du document : elle apparaît à
        l'intérieur d'un container, déjà chargé de repères. Seul le « + » capte
        la souris : la bande chevauche les box voisines (bas de la dernière
        ligne, haut de la suivante quand la marge est fine), qu'un clic aurait
        sinon transformé en ajout de ligne. */}
    {showToolbar && <div
      ref={adderBandRef}
      data-outside-ignore="true"
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: adderBandBottom,
        height: adderBandHeight,
        zIndex: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
        backgroundColor: isAdderHovered
          ? 'rgba(64, 150, 255, 0.16)'
          : 'rgba(64, 150, 255, 0.07)',
        transition: 'background-color 150ms ease',
        // Trou sur la poignée du bas de la box sélectionnée (cf. hook).
        clipPath: adderClearance.clipPath,
      }}
    >
      <div style={{
        position: 'absolute',
        left: 0,
        right: 0,
        height: 1,
        backgroundColor: 'rgba(64, 150, 255, 0.3)',
      }} />
      <Button
        shape='circle'
        tabIndex={-1}
        title="Ajouter une ligne en dessous"
        onMouseOver={() => setIsAdderHovered(true)}
        onMouseLeave={() => setIsAdderHovered(false)}
        onClick={(e) => {
          e.stopPropagation();
          addRowToColumn({ columnId: parentColumnId, index: rowIndex })
        }}
        style={{
          ...adderButtonStyle,
          pointerEvents: 'auto',
          transform: `translateX(${adderClearance.plusShift}px)`,
        }}
        icon={<FontAwesomeIcon icon={icon({ name: 'plus', style: 'solid' })} style={{ fontSize: 10, display: 'block' }} />}
      />
    </div>}
  </div>
})

export default ColumnsRow
